'use client';

import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useLocale } from 'next-intl';
import type { QueryParams } from '@/lib/api/httpClient';
import { createHttpClient } from '@/lib/api/httpClient';
import {
  mapFuturesOpenPositionsPage,
  mapFuturesPositionHistoryPage,
  mapOpenPositionsPage,
  mapPositionHistoryPage,
  mapProfileInfoResponse,
} from './profile-mappers';
import type {
  FuturesOpenPosition,
  FuturesPositionHistory,
  PaginatedItems,
  PositionsFilterParams,
  ProfileInfoResponse,
  ProfileMarket,
  ProfileOpenPosition,
  ProfilePositionHistory,
  ProfilePositionsTab,
  ProfitLossPeriod,
  ProfitLossResponse,
} from './profile-types';

const BASE_URL = process.env.NEXT_PUBLIC_ORDER_HISTORY_URL;

const http = createHttpClient({ baseUrl: BASE_URL });

// ── Query keys ───────────────────────────────────────────────────────

export const PROFILE_INFO_QK = 'profile-info';
export const PROFIT_LOSS_QK = 'profit-loss';
export const PROFILE_POSITIONS_QK = 'profile-positions';

// ── Fetch functions ──────────────────────────────────────────────────

async function fetchProfileInfoClient(
  userId: string,
  market?: ProfileMarket,
): Promise<ProfileInfoResponse> {
  const raw = await http.get<Record<string, unknown>>(`/history/stats/profile/${userId}`, {
    query: market ? { market } : undefined,
  });
  return mapProfileInfoResponse(raw);
}

async function fetchProfitLossClient(
  userId: string,
  period?: ProfitLossPeriod,
  market?: ProfileMarket,
): Promise<ProfitLossResponse> {
  const params: QueryParams = {};
  if (period) params.period = period;
  if (market) params.market = market;

  return http.get<ProfitLossResponse>(`/history/stats/profile/${userId}/profit-loss`, {
    query: params,
  });
}

function buildPositionsQuery(
  tab: ProfilePositionsTab | undefined,
  page: number | undefined,
  size: number | undefined,
  filters: PositionsFilterParams | undefined,
  market: ProfileMarket | undefined,
  locale?: string,
): QueryParams {
  const params: QueryParams = {};
  if (market) params.market = market;
  // Backend localizes position/outcome names when `locale` is passed.
  if (locale) params.locale = locale;
  if (tab) params.tab = tab;
  if (page !== undefined) params.page = page;
  if (size !== undefined) params.size = size;
  if (filters?.openDatePreset) params.openDatePreset = filters.openDatePreset;
  if (filters?.closeDatePreset) params.closeDatePreset = filters.closeDatePreset;
  if (filters?.pnlSign) params.pnlSign = filters.pnlSign;
  if (filters?.pnlType) params.pnlType = filters.pnlType;
  if (filters?.pnlPercentFrom !== undefined) params.pnlPercentFrom = filters.pnlPercentFrom;
  if (filters?.pnlPercentTo !== undefined) params.pnlPercentTo = filters.pnlPercentTo;
  if (filters?.search) params.search = filters.search;
  if (filters?.sort) {
    params.sort = filters.sort;
    if (filters.order) params.order = filters.order;
  }
  return params;
}

/** The endpoint returns one paged list `{ i, p, s, tp, ti }` for the requested market+tab. */
async function fetchPositionsRaw(
  userId: string,
  market: ProfileMarket,
  tab: ProfilePositionsTab,
  page: number,
  size: number,
  filters?: PositionsFilterParams,
  locale?: string,
): Promise<Record<string, unknown>> {
  return http.get<Record<string, unknown>>(`/history/stats/profile/${userId}/positions`, {
    query: buildPositionsQuery(tab, page, size, filters, market, locale),
  });
}

/** First page of a player's closed predictions, filtered. */
export async function fetchPredictionHistoryPage(
  userId: string,
  size: number,
  filters?: PositionsFilterParams,
): Promise<PaginatedItems<ProfilePositionHistory> | null> {
  const raw = await fetchPositionsRaw(userId, 'PREDICTION', 'POSITION_HISTORY', 0, size, filters);
  return mapPositionHistoryPage(raw as never);
}

/** First page of a player's closed futures positions, filtered. */
export async function fetchFuturesHistoryPage(
  userId: string,
  size: number,
  filters?: PositionsFilterParams,
): Promise<PaginatedItems<FuturesPositionHistory> | null> {
  const raw = await fetchPositionsRaw(userId, 'EXCHANGE', 'POSITION_HISTORY', 0, size, filters);
  return mapFuturesPositionHistoryPage(raw as never);
}

// ── Hooks ────────────────────────────────────────────────────────────

export function useProfileInfo(
  userId: string,
  market?: ProfileMarket,
  initialData?: ProfileInfoResponse | null,
) {
  return useQuery<ProfileInfoResponse>({
    queryKey: [PROFILE_INFO_QK, userId, market ?? null],
    queryFn: () => fetchProfileInfoClient(userId, market),
    initialData: initialData || undefined,
    initialDataUpdatedAt: initialData ? Date.now() : undefined,
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
}

export function useProfitLoss(
  userId: string,
  period: ProfitLossPeriod,
  market?: ProfileMarket,
  initialData?: ProfitLossResponse | null,
) {
  return useQuery<ProfitLossResponse>({
    queryKey: [PROFIT_LOSS_QK, userId, period, market ?? null],
    queryFn: () => fetchProfitLossClient(userId, period, market),
    initialData: initialData || undefined,
    initialDataUpdatedAt: initialData ? Date.now() : undefined,
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
}

// ── Infinite-scroll position lists ───────────────────────────────────

/** Next 0-based page, or undefined when the last page has been loaded. */
function nextPageParam<T>(lastPage: PaginatedItems<T> | null): number | undefined {
  if (!lastPage) return undefined;
  const next = lastPage.page + 1;
  return next < lastPage.totalPages ? next : undefined;
}

/** Wrap an SSR-fetched first page into the infinite-query cache shape. */
function seedPages<T>(initialPage?: PaginatedItems<T> | null) {
  return initialPage ? { pages: [initialPage], pageParams: [0] } : undefined;
}

export function useOpenPositions(
  userId: string,
  size: number,
  filters?: PositionsFilterParams,
  initialPage?: PaginatedItems<ProfileOpenPosition> | null,
) {
  const locale = useLocale();
  return useInfiniteQuery({
    queryKey: [PROFILE_POSITIONS_QK, 'PREDICTION', userId, 'OPEN_POSITIONS', size, filters, locale],
    queryFn: async ({ pageParam }) => {
      const raw = await fetchPositionsRaw(
        userId,
        'PREDICTION',
        'OPEN_POSITIONS',
        pageParam,
        size,
        filters,
        locale,
      );
      return mapOpenPositionsPage(raw as never);
    },
    // A guest has no id: the header counters pass '' and expect no request.
    enabled: Boolean(userId),
    initialPageParam: 0,
    getNextPageParam: nextPageParam,
    initialData: seedPages(initialPage),
    initialDataUpdatedAt: initialPage ? Date.now() : undefined,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
}

/** One page covers every open position a player realistically holds. */
const OPEN_POSITIONS_COUNT_PAGE = 100;

/**
 * The open prediction positions' count and first page. The header's My positions
 * badge and the balance popover share this one query (and cache).
 */
export function useOpenPositionsCount(userId: string) {
  const { data, isLoading } = useOpenPositions(userId, OPEN_POSITIONS_COUNT_PAGE);
  const firstPage = data?.pages[0] ?? null;
  const count = firstPage?.totalItems ?? firstPage?.items.length ?? 0;
  return { count, firstPage, isLoading };
}

export function usePositionHistory(
  userId: string,
  size: number,
  filters?: PositionsFilterParams,
  initialPage?: PaginatedItems<ProfilePositionHistory> | null,
) {
  const locale = useLocale();
  return useInfiniteQuery({
    queryKey: [
      PROFILE_POSITIONS_QK,
      'PREDICTION',
      userId,
      'POSITION_HISTORY',
      size,
      filters,
      locale,
    ],
    queryFn: async ({ pageParam }) => {
      const raw = await fetchPositionsRaw(
        userId,
        'PREDICTION',
        'POSITION_HISTORY',
        pageParam,
        size,
        filters,
        locale,
      );
      return mapPositionHistoryPage(raw as never);
    },
    initialPageParam: 0,
    getNextPageParam: nextPageParam,
    initialData: seedPages(initialPage),
    initialDataUpdatedAt: initialPage ? Date.now() : undefined,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
}

export function useFuturesOpenPositions(
  userId: string,
  size: number,
  filters?: PositionsFilterParams,
  initialPage?: PaginatedItems<FuturesOpenPosition> | null,
) {
  const locale = useLocale();
  return useInfiniteQuery({
    queryKey: [PROFILE_POSITIONS_QK, 'EXCHANGE', userId, 'OPEN_POSITIONS', size, filters, locale],
    queryFn: async ({ pageParam }) => {
      const raw = await fetchPositionsRaw(
        userId,
        'EXCHANGE',
        'OPEN_POSITIONS',
        pageParam,
        size,
        filters,
        locale,
      );
      return mapFuturesOpenPositionsPage(raw as never);
    },
    initialPageParam: 0,
    getNextPageParam: nextPageParam,
    initialData: seedPages(initialPage),
    initialDataUpdatedAt: initialPage ? Date.now() : undefined,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
}

export function useFuturesPositionHistory(
  userId: string,
  size: number,
  filters?: PositionsFilterParams,
  initialPage?: PaginatedItems<FuturesPositionHistory> | null,
) {
  const locale = useLocale();
  return useInfiniteQuery({
    queryKey: [PROFILE_POSITIONS_QK, 'EXCHANGE', userId, 'POSITION_HISTORY', size, filters, locale],
    queryFn: async ({ pageParam }) => {
      const raw = await fetchPositionsRaw(
        userId,
        'EXCHANGE',
        'POSITION_HISTORY',
        pageParam,
        size,
        filters,
        locale,
      );
      return mapFuturesPositionHistoryPage(raw as never);
    },
    initialPageParam: 0,
    getNextPageParam: nextPageParam,
    initialData: seedPages(initialPage),
    initialDataUpdatedAt: initialPage ? Date.now() : undefined,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
}
